import { bulkExportStatusEnum } from "../common/enum/bulkExportStatusEnum";

// Regex Unicode
const allowedCharRegex = /[\p{L}\p{N}\.,]/u; // modern browser
const fallbackRegex = /[A-Za-z0-9\.,]/; // old browser
const symbolsRegex = /[.,<>[\]{}\+\-\*\/\\|:;'"()_=`~!?@#$%^&]/; // symbols
const specialKeys = ['Backspace', 'Delete', 'Space', 'Enter', 'NumpadEnter']; //special keys
export class Util {


    /**
     * Clone a typescript object deeply
     * @param obj Object to clone.
     * @returns Cloned object.
     */
    public static deepClone<T>(obj: T): T {
        if (obj === null || typeof obj !== "object") {
            return obj;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => Util.deepClone(item)) as unknown as T;
        }

        const result: any = {};
        for (const key in obj) {
            result[key] = Util.deepClone((obj as any)[key]);
        }
        return result;
    }

    /**
     * Converts a given string to camel case format.
     * @param str The input string to convert.
     * @returns The camelCase formatted string.
     */
    static toCamelCase(str: string): string {
        return str
            .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
                index === 0 ? match.toUpperCase() : match.toLowerCase()
            )
            .replace(/\s+/g, ''); // Remove spaces
    }

    /**
     * Format numeric file size in bytes to text representation -> e.g. 30.05B, 890.51KB, 5.00MB or null
     * bytes - numeric bytes size
     * decimal - optional decimals, by default 2 decimals
     * zeroToNull - optional, true -> converts size 0 to null, otherwise remains 0, by default false
     */
    static formatFileSize(bytes: number | null | undefined, decimals = 2, zeroToNull = false): string {
        if (bytes === null || bytes === undefined) { return ''; }
        if (bytes < 0) { bytes = 0; }

        if (zeroToNull && bytes === 0) {
            return '';
        }

        const KB = 1024;
        const MB = 1024 * KB;

        if (bytes < MB) {
            const kbValue = bytes / KB;
            return `${kbValue.toFixed(decimals)}KB`;
        }

        const mbValue = bytes / MB;
        return `${mbValue.toFixed(decimals)}MB`;
    }

    static formatProgress(pct: number | null | undefined): string {
        const value = pct ?? 0;
        return `${value}%`;
    }

    static validKeyBoard(event: any) {

        const modifierPressed = (event.getModifierState && (
            event.getModifierState('Control') ||
            event.getModifierState('Alt') ||
            event.getModifierState('Meta') ||
            event.getModifierState('AltGraph')
        )) || event.ctrlKey || event.altKey || event.metaKey;

        // Consider these names as "modifier-only" keys
        const modifierNames = ['Control', 'Alt', 'Meta', 'Shift', 'AltGraph'];

        // If a modifier is pressed AND the released key is not a modifier itself,
        // treat it as a combo and ignore it.
        if (modifierPressed && !modifierNames.includes(event.key)) {
            // ignore combos like Ctrl+S, Alt+F, AltGraph+q
            return false;
        }

        let key = event.key;
        let code = event.code;
        if (specialKeys.includes(code)) return true;
        if (key.length === 1) {
            if (allowedCharRegex.test(key)) return true;
            if (symbolsRegex.test(key)) return true
            if (fallbackRegex.test(key)) return true;
        }
        return false
    }

    /**
     * Helper function to decode seconds to friendly text for Bulk Export
     */
    static secondsToFriendlyText(totalSeconds: number | null): string {
        
        if (totalSeconds == null) {
            return 'estimating time to complete...';
        }

        const minutes = Math.round(totalSeconds / 60);
        const hours = Math.round(totalSeconds / 3600);
        const days = Math.round(totalSeconds / 86400);

        if (totalSeconds < 10)
            return 'momentarily';
        if (totalSeconds < 30)
            return 'about half a Minute';
        if (minutes < 2)
            return 'about a Minute';
        if (minutes < 25)
            return 'under ' + minutes + ' Minutes';
        if (minutes < 40)
            return 'about half an Hour';
        if (minutes < 65)
            return 'about an Hour';
        if (hours < 2)
            return 'little over an Hour';
        if (hours < 20)
            return 'under ' + hours + ' Hours';
        if (hours < 26)
            return 'about a Day';
        if (days < 2)
            return 'little over a Day';
        return 'under ' + days + ' Days';
    }

    /**
     * Decodes the status In progress to the statuses with time representation In Progress: momentarilly, In progress: under 2 minutes etc. 
     */
    static decodeStatusBulkExport(item: any): string {
        const status = item?.status;
        const statusDecoded: string = item?.statusDecoded ?? '';

        if (status !== bulkExportStatusEnum.IN_PROGRESS_STARTED && 
            status !== bulkExportStatusEnum.IN_PROGRESS
        ) {
            return statusDecoded;
        }

        if (Number.isNaN(Number(item?.pendingToCompleteSeconds))) {
            //undefinied, text -> fallback to original decoded status
            return statusDecoded;
        }
    
        //number or null
        const friendlyText = Util.secondsToFriendlyText(item?.pendingToCompleteSeconds);
        return `${statusDecoded}: ${friendlyText}`;
    }

    static markAppAsReady(): void {
        const body = document.body;
        body.classList.remove('initial-loading');
        body.classList.add('app-ready');

        const appRootElement = document.querySelector('app-root');
        if (appRootElement) {
            appRootElement.classList.remove('initial-loading');
            appRootElement.classList.add('app-ready');
        }
    }

    static normalizeMeddra(value: string): string {
        // Matches exactly two digits (nn)
        const integerPattern = /^\d{2}$/;

        if (integerPattern.test(value)) {
            return `${value}.0`;
        } else {
            return `${value}`;
        }
    }

    static denormalizeMeddra(value: string): string {
        // Matches exactly two digits followed by .0 (nn.0)
        const normalizedPattern = /^\d{2}\.0$/;

        if (normalizedPattern.test(value)) {
            return value.replace('.0', '');
        }

        return value;
    }

}